// Inbound URLs for the retired per-line signals page (AB#5977).
//
// /line/:id/signals is gone, but years of alert emails, bookmarks and shared
// links still point at it, so the route survives as a permanent redirect and
// this is the translation it uses. Links the app builds itself go through
// buildSignalExplorerUrl instead — nothing new should speak this dialect.

// Time-window params both pages understood, carried across the redirect.
const CARRIED_PARAMS = ['time_range', 'start_date', 'end_date'] as const

// One-shot marker: the explorer, on entry, seeds the line/equipment's favourite
// signals (see useDefaultFavorites) so a redirect with no explicit selection
// lands on the same default the retired page showed instead of an empty
// workspace.
export const DEFAULT_FAVORITES_PARAM = 'defaultFavorites'

// Classic → explorer: equipment→equipmentId, the path lineId→the lineId filter,
// carry the time window. An explicit classic selection (?signal=<ids>) becomes
// the explorer's ?sig=; without one we mark ?defaultFavorites=1. Classic-only
// params (tab/batch/anomaly/view/skip_initial_loading) are dropped — the
// explorer has no notion of them.
export const reshapeLegacyToExplorer = (lineId: string, search: URLSearchParams): string => {
  const next = new URLSearchParams()
  if (lineId) {
    next.set('lineId', lineId)
  }
  const equipment = search.get('equipment')
  if (equipment) {
    next.set('equipmentId', equipment)
  }
  for (const key of CARRIED_PARAMS) {
    const value = search.get(key)
    if (value) {
      next.set(key, value)
    }
  }
  // Batch modal → program overlay. The batch "View signals" links carry
  // program_id + batch_mode (last_10 | current); map them to the explorer's
  // program context so the run comparison is set up on arrival instead of an
  // empty time view.
  const programId = search.get('program_id')
  if (programId) {
    next.set('prog', programId)
    if (search.get('batch_mode') === 'last_10') {
      // Compare the last 10 runs = overlay them on elapsed time.
      next.set('rule', 'lastN:10')
      next.set('overlay', '1')
    } else if (search.get('batch_mode') === 'current') {
      // Frame the single batch by its own window (shade, no overlay).
      next.set('rule', 'window')
      const batchStart = search.get('batch_start_date')
      const batchEnd = search.get('batch_end_date')
      if (batchStart) {
        next.set('start_date', batchStart)
      }
      if (batchEnd) {
        next.set('end_date', batchEnd)
      }
    }
  }
  const signal = search.get('signal')
  if (signal) {
    // Explicit selection wins — classic '?signal=' is csv, same as '?sig='.
    next.set('sig', signal)
  } else {
    next.set(DEFAULT_FAVORITES_PARAM, '1')
  }
  return `/signal-explorer?${next.toString()}`
}
